import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import countriesData from '../list_data/countries_en.json';
import '../styles/Sidebar.css';

const UserProfilePopover = ({
  userData,
  onLogout,
  onClose,
  onNavigate,
  isCollapsed = false,
  showEmail = true,
  showActions = true,
  showCountry = true,
  showLearningGoal = false,
  showAddFriend = false,
  addFriendStatus = 'idle',
  onAddFriend,
  variant = 'sidebar',
  onMeasure,
  isTutorMode = false,
  currentLanguage = '',
}) => {
  const { t } = useTranslation();
  const popoverRef = useRef(null);
  const [localAvatarLoaded, setLocalAvatarLoaded] = useState(false);
  const [localAvatarError, setLocalAvatarError] = useState(false);

  // Helper to extract Y index and convert to percentage
  const getBackgroundPosition = (url) => {
    if (!url) return 'center center';
    // Supports both old (background_123.png) and versioned (background_123_v2.png) formats.
    const match = url.match(/background_(\d+)(?:_v\d+)?\.png(?:\?.*)?$/i);
    if (match && match[1]) {
      const yIndex = parseInt(match[1], 10);
      // Clamp between 0 and 1000, then convert to percentage
      const percentage = Math.min(100, Math.max(0, (yIndex / 1000) * 100));
      return `center ${percentage}%`;
    }
    return 'center center';
  };

  const getCountryName = (code) => {
    if (!code) return 'N/A';
    // Use loose equality or toUpperCase for robust matching if needed
    const found = countriesData.find(c => c.name === code || c.code === code);
    return found ? found.name : code;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        onClose?.();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    setLocalAvatarLoaded(false);
    setLocalAvatarError(false);
  }, [userData?.avatarUrl]);

  useEffect(() => {
    if (!popoverRef.current) return;
    if (typeof onMeasure !== 'function') return;
    const element = popoverRef.current;
    const observer = new ResizeObserver(() => {
      const rect = element.getBoundingClientRect();
      onMeasure(rect);
    });
    observer.observe(element);
    const rect = element.getBoundingClientRect();
    onMeasure(rect);
    return () => {
      observer.disconnect();
    };
  }, [onMeasure]);

  if (!userData) return null;

  const { email, username } = userData;
  const country = userData.country || userData.settings?.country || '';
  // ✨ Use default avatar if no URL provided
  const avatarUrl = userData.avatarUrl || '/assets/default_avatar.jpeg';
  const backgroundUrl = userData.backgroundUrl || '';
  
  const isAvatarLoaded = typeof userData.isAvatarLoaded === 'boolean'
    ? userData.isAvatarLoaded
    : localAvatarLoaded;
  
  const showAvatarSkeleton = !isAvatarLoaded;

  const displayCountry = getCountryName(country);
  const learningGoal = userData.learning_goal || userData.learningGoal || 'N/A';

  const sidebarStateClass = variant === 'sidebar'
    ? (isCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded')
    : '';

  return (
    <>
      <div className="user-popover-overlay" onClick={onClose} />
      <div
        className={`user-popover ${variant === 'friends' ? 'friends-popover' : ''} ${sidebarStateClass}`}
        ref={popoverRef}
      >
        {showAddFriend && (
          <button
            className={`popover-action-btn ${addFriendStatus === 'sent' ? 'is-sent' : ''}`}
            type="button"
            onClick={onAddFriend}
            disabled={addFriendStatus === 'sent'}
          >
            {addFriendStatus === 'sent' ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M12 5v14" />
                <path d="M5 12h14" />
              </svg>
            )}
          </button>
        )}
        <div className="popover-banner">
          {backgroundUrl && (
            <img
              className="popover-banner-img"
              src={backgroundUrl}
              alt=""
              loading="lazy"
              decoding="async"
              aria-hidden="true"
              style={{ objectPosition: getBackgroundPosition(backgroundUrl) }}
            />
          )}
        </div>
        <div className="popover-header">
          <div className="popover-avatar">
            {showAvatarSkeleton && (
              <div className={`popover-avatar-skeleton ${isAvatarLoaded ? 'is-hidden' : ''}`} />
            )}
            <img
              className={`popover-avatar-img ${isAvatarLoaded ? 'is-visible' : ''}`}
              src={avatarUrl}
              alt={t('profilePage.avatarAlt', 'User avatar')}
              onLoad={userData.onAvatarLoad || (() => setLocalAvatarLoaded(true))}
              onError={(e) => {
                // Fallback to default if load fails
                e.target.src = '/Resources/imagine/default_avatar/default_avatar.jpeg';
                if (userData.onAvatarError) userData.onAvatarError();
                else setLocalAvatarError(true);
              }}
            />
          </div>
        </div>

        <div className="popover-body">
          <div className="popover-name-box">
            <div className="popover-username">{username || 'Guest'}</div>
            {showEmail && <div className="popover-email">{email}</div>}
          </div>

          {(showCountry || showLearningGoal) && (
            <div className="popover-details">
              {showCountry && (
                <div className="detail-row">
                  <span className="detail-label">{t('profilePage.country', 'Country')}</span>
                  <span className="detail-value">{displayCountry}</span>
                </div>
              )}
              {showLearningGoal && (
                <div className={`detail-row ${learningGoal.length > 20 ? 'align-start' : ''}`}>
                  <span className="detail-label">{t('profilePage.learningGoal', 'Learning Goal')}</span>
                  <span className="detail-value learning-goal">
                    {learningGoal}
                  </span>
                </div>
              )}
            </div>
          )}

          {showActions && (
            <div className="popover-actions">
              <button
                className="popover-btn"
                onClick={() => {
                    if (isTutorMode && currentLanguage) {
                        onNavigate?.(`/tutor/workspace/${currentLanguage}/profile`);
                    } else {
                        onNavigate?.('/profile');
                    }
                  onClose?.();
                }}
              >
                {t('header.profile', 'Settings')}
              </button>
              <button
                className="popover-btn danger"
                onClick={onLogout}
              >
                {t('header.logout', 'Log Out')}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default UserProfilePopover;
